import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import TooltipSimple from '../../../Common/TooltipSimple';
import { Activite, Thematique } from '../../../../../../common/src/entities';
import { Accordion, Card, Popover } from 'react-bootstrap';
import { BiCheckCircle, BiMap, BiXCircle } from 'react-icons/all';
import { affichageLieu } from '../../../Dashboard/Lieux/DetailsLieu';
import { BiCalendar, BiDollarCircle, BiGroup, BiInfoCircle, BiUser } from 'react-icons/bi';

export type DetailInfo = {
    titre?: string;
    texte: ReactNode;
    icone?: ReactElement;
    tooltip?: ReactNode;
    long?: boolean;
    optionnel?: boolean;
    hide?: boolean;
};

export type SectionInfo = {
    icone?: ReactElement;
    titre: string;
    texte?: ReactNode;
    champs?: DetailInfo[];
    noHr?: boolean;
    hide?: boolean;
    accordeon?: boolean;
};

export type SectionAdmin = {
    icone?: ReactElement;
    titre: string;
};

/**
 * Retourne les sections. La premiere section est la carte (Informations importantes)
 * @param activite
 */
export function getSections(activite: Activite): SectionInfo[] {
    let lieuAnimation: ReactNode = '';

    switch (activite.typeActivite.name) {
        case 'Entrée':
            lieuAnimation = "À l'école";
            break;
        case 'Sortie':
            lieuAnimation =
                "Consultez votre contrat pour le lieu choisi.\n\
Solution Animation peut faire la réservation de transport (autobus) pour vous.\n\
Si vous réservez le transport vous-même, donnez le lieu d'activité à votre transporteur." +
                (activite.guide_type_accueil ? `\n${activite.guide_type_accueil}` : '');
            break;
        case 'Livraison':
            lieuAnimation = "Les produits seront livrés à l'école";
            break;
    }

    let changementPrix: ReactNode = activite.guide_changement_prix;

    const horaireType: ReactNode = activite.horaire_type
        ? `${activite.horaire_type}\nL'horaire exact peut varier.`
        : activite.horaire_type;

    switch (activite.guide_changement_prix) {
        case null:
        case 'Aucune modification':
            changementPrix = null;
            break;
        case 'Possible':
            changementPrix = `Les prix sont habituellement majorés une fois par année. Certains fournisseurs acceptent des réservations, \
mais appliqueront les prix valides au moment de la livraison des services.`;
            break;
    }

    function extraireLangue(): string[] {
        const temp = activite.langues.map((langue) => langue.name);
        return temp;
    }
    let typeTooltip: ReactNode = '';
    switch (activite.typeActivite.name) {
        case 'Entrée':
            typeTooltip = "Activité qui se déplace. Exemple : un magicien à l'école.";
            break;
        case 'Sortie':
            typeTooltip =
                'Activité qui vous reçoit dans ses locaux Exemple : aller au Centre des Sciences de Montréal.';
            break;
        case 'Livraison':
            typeTooltip = 'Produit qui est livré à l&apos;école Exemple : livraison de toutous à l&apos;école.';
            break;
        default:
            typeTooltip =
                ' Activité qui requiert une connexion à Internet et un ordinateur Exemple : Instructions vidéo pour faire une expérience scientifique';
            break;
    }

    return [
        {
            titre: 'Informations importantes',
            champs: [
                {
                    titre: 'Type',
                    texte: activite.typeActivite?.name,
                    icone: <BiInfoCircle className="mb-1" />,
                    tooltip: typeTooltip,
                },
                {
                    titre: 'Ouverture',
                    texte: activite.periode_ouverture,
                    icone: <BiCalendar className="mb-1" />,
                    tooltip: "Période d'ouverture",
                },
                {
                    titre: 'Minimum',
                    texte: activite.minimum_facturable,
                    icone: <BiUser className="mb-1" />,
                    tooltip: 'Vous pouvez avoir moins de participants, mais ce sera le minimum facturable.',
                },
                {
                    titre: 'Maximum',
                    texte: activite.max_participants_visible ? activite.vitrine_maximum_participants : null,
                    icone: <BiGroup className="mb-1" />,
                    tooltip:
                        "C'est la capacité d'accueil du fournisseur. Votre nombre de participants réservé ne peut dépasser ce nombre.",
                    optionnel: true,
                },
                {
                    titre: 'Prix',
                    texte: activite.tarifs_estimes,
                    icone: <BiDollarCircle className="mb-1" />,
                    tooltip: (
                        <>
                            Approximation du prix en journée pédagogique.
                            <br />
                            Le prix réel peut varier
                            <br />
                            (ex. km, nombre jeunes).
                        </>
                    ),
                },
            ],
            noHr: true,
        },
        { titre: "Description de l'activité", noHr: true, texte: activite.description },
        {
            titre: 'COVID-19',
            texte: activite.covid_description,
            icone:
                activite.covid == 'Oui' ? (
                    <BiCheckCircle className="mb-1 text-success" />
                ) : (
                    <BiXCircle className="mb-1 text-danger" />
                ),

            hide: activite.covid == null || activite.covid == 'N/A',
        },
        {
            titre: 'Thématiques',
            texte: <small className="text-muted">Cliquez sur une thématique pour la description</small>,
            accordeon: true,
            champs: activite.thematiques
                .filter((thematique) => thematique.est_public !== false)
                .sort((a, b) => a.ordreAffichage - b.ordreAffichage)
                .map((thematique) => ({
                    texte: thematique.description,
                    titre: thematique.name,
                    long: true,
                })),
            hide: !activite.thematiques || activite.thematiques.length == 0,
        },
        {
            titre: 'Lieux',
            champs: activite.lieux?.map((lieu) => ({ texte: affichageLieu(lieu.lieu), icone: <BiMap /> })),
            hide: activite.typeActivite?.name !== 'Sortie',
        },
        {
            titre: 'Détails',
            champs: [
                { long: true, optionnel: true, titre: "Lieu d'animation", texte: lieuAnimation },
                {
                    long: true,
                    optionnel: true,
                    titre: "Confirmation de l'horaire",
                    texte: activite.guide_creation_horaire,
                },
                { long: true, optionnel: true, titre: 'Public cible', texte: activite.public_cible },
                {
                    long: true,
                    optionnel: true,
                    titre: "Minimum facturable le jour de l'animation",
                    texte: activite.guide_minimum_facturable_jour_anim_client,
                },
                {
                    long: true,
                    optionnel: true,
                    titre: "Minimum facturable le jour de l'animation",
                    texte: activite.guide_minimum_facturable_jour_anim_fournisseur,
                    hide: true,
                },
                {
                    long: true,
                    optionnel: true,
                    titre: 'Arrivée des animateurs sur place',
                    texte: activite.temps_avance_animateur,
                },
                { long: true, optionnel: true, titre: 'Ratio et formation des groupes', texte: activite.ratio_groupe },
                { long: true, optionnel: true, titre: 'Locaux requis', texte: activite.locaux },
                {
                    long: true,
                    titre: "Langues d'animations possibles ",
                    texte: extraireLangue().join(' & '),
                    hide: !activite.langues || activite.langues.length == 0,
                },
                {
                    long: true,
                    optionnel: true,
                    titre: 'Changement de prix entre la réservation et l’animation',
                    texte: changementPrix,
                },
                {
                    long: true,
                    optionnel: true,
                    titre: 'Modification des prix',
                    texte: `Les prix sont conditionnels aux modifications de la part du fournisseur de l'activité ${activite.name}`,
                },
            ],
        },
        {
            titre: 'Horaire type',
            texte: horaireType,
            hide: !activite.horaire_type,
        },
        {
            titre: 'Comment bien vous préparer à l’activité',
            texte: activite.guide_preparation,
            hide: !activite.guide_preparation,
        },
        {
            titre: 'Questionnaire logistique',
            texte: activite.questionnaire_logistique,
            hide: !activite.questionnaire_logistique,
        },
        {
            titre: 'Clauses et politiques du fournisseur',
            texte: activite.guide_clauses_politiques,
            hide: !activite.guide_clauses_politiques,
        },
        { titre: 'FAQ', texte: activite.guide_FAQ, hide: !activite.guide_FAQ },
    ];
}
function genererContenuPopover(typeActiviteNom: string) {
    let description = '';

    switch (typeActiviteNom) {
        case 'Entrée':
            description = 'Description pour le type Entrée.';
            break;
        case 'Sortie':
            description = 'Description pour le type Sortie.';
            break;
        case 'Livraison':
            description = 'Description pour le type Livraison.';
            break;
        default:
            description = 'Description par défaut.';
            break;
    }

    return (
        <Popover id={`popover-${typeActiviteNom}`}>
            <Popover.Title>{typeActiviteNom}</Popover.Title>
            <Popover.Content>{description}</Popover.Content>
        </Popover>
    );
}

export function Detail(props: DetailInfo): ReactElement | null {
    const { optionnel, texte, tooltip, icone, titre, long, hide } = props;
    return !optionnel || texte ? (
        <>
            {hide ? null : (
                <div className="mb-2">
                    <TooltipSimple texte={tooltip} disabled={!tooltip}>
                        <a>
                            {icone ? <span className="mr-1">{icone}</span> : null}
                            <b>{titre ? <>{titre} :</> : null}</b>
                        </a>
                    </TooltipSimple>{' '}
                    <span style={{ whiteSpace: 'pre-line' }}>{long ? <p className="mb-1">{texte}</p> : texte}</span>
                </div>
            )}
        </>
    ) : null;
}

export function Separateur(): ReactElement {
    return <hr className="my-4" />;
}

export function Section(props: SectionInfo): ReactElement | null {
    const { titre, texte, champs, noHr, hide, icone, accordeon } = props;
    const content = typeof texte == 'string' ? <p style={{ whiteSpace: 'pre-line' }}>{texte}</p> : texte;
    return hide ? null : (
        <>
            {!noHr && <Separateur />}
            <h5 className="mb-3">
                {titre} {icone}
            </h5>
            {content}
            {accordeon ? (
                <Accordion>
                    {champs?.map((thematique, index) => (
                        <Card key={thematique.titre}>
                            <Accordion.Toggle
                                as={Card.Header}
                                eventKey={index.toString()}
                                style={{ cursor: 'pointer' }}
                            >
                                {thematique.titre}
                            </Accordion.Toggle>
                            <Accordion.Collapse eventKey={index.toString()}>
                                {thematique.texte ? (
                                    <Card.Body>
                                        <p style={{ whiteSpace: 'pre-line' }}>{thematique.texte}</p>
                                    </Card.Body>
                                ) : (
                                    <></>
                                )}
                            </Accordion.Collapse>
                        </Card>
                    ))}
                </Accordion>
            ) : (
                champs?.map((champ, index) => <Detail key={index} {...champ} />)
            )}
        </>
    );
}
